import { useEffect, useState } from 'react';
import type { GetServerSidePropsContext } from 'next';
import type { ParsedUrlQuery } from 'querystring';
import { addDays } from 'date-fns';

import type { DestroyCookie, SetCookie } from '@packages/utilities';
import {
  useConfig,
  useCookies,
  nookieSetCookie,
  nookieDestroyCookie,
  nookieParseCookies,
} from '@packages/utilities';

// import useCookieConsent from '../useCookieConsent';

const apiUrl =
  typeof window === 'undefined'
    ? 'http://lackmusapi.apigateway.empirie.<env>/lackmus/api'
    : '/lackmus/api';

const cookiePrefix = 'mvt_';
const participantSuffix = '_participant';

export const getTestCookieName = (testId: string) => `${cookiePrefix}${testId}`;
export const getParticipantCookieName = (testId: string) =>
  `${getTestCookieName(testId)}${participantSuffix}`;

export type OutcomePayload = Record<string, string | number>;

export type TestRuleset =
  | {
      participant: string;
      variant: Record<string, string>;
    }
  | undefined;

export type Product = {
  sku: string;
  masterId?: string; // AKL
  count: number;
  value: number;
};

export type Payload = Record<string, string | number>;

export type Template = 'ATB' | 'RTH' | 'RPP';

export type APIPayload = {
  client: string;
  test: string;
  uuid: string;
  conversion: Array<string | number>;
  value: Array<string | number>;
  'User-Agent': string;
};

export const getAllTestParticipations = (cookies: Record<string, string>) => {
  const testIds = Object.keys(cookies).reduce((accumulator, key) => {
    if (!key.startsWith(cookiePrefix)) return accumulator;

    const testId = key.replace(cookiePrefix, '').replace(participantSuffix, '');

    if (!accumulator.includes(testId)) {
      accumulator.push(testId);
    }

    return accumulator;
  }, [] as string[]);

  return testIds.map((testId) => {
    const fullCookieKey = `${cookiePrefix}${testId}`;

    if (cookies[fullCookieKey]) {
      const { participant, variant } = JSON.parse(cookies[fullCookieKey]) as {
        participant: string;
        variant: Record<string, string>;
      };

      return { testId, participantId: participant, variant };
    }

    const participantCookieKey = getParticipantCookieName(testId);

    return {
      testId,
      participantId: cookies[participantCookieKey],
    };
  });
};

type SetTestCookiesParameters = {
  context?: GetServerSidePropsContext<ParsedUrlQuery>;
  testId: string;
  cookies: Record<string, string>;
  setCookie: SetCookie;
  value: {
    participant: string;
    variant: Record<string, string>;
  };
};

export const setTestCookies = ({
  context,
  testId,
  cookies,
  setCookie,
  value,
}: SetTestCookiesParameters) => {
  // expiry date once for in a day and once in 30 days
  const now = new Date();

  const testCookieName = getTestCookieName(testId);

  setCookie(context, testCookieName, JSON.stringify(value), {
    expires: addDays(now, 1),
    path: '/',
  });

  const participantCookieName = getParticipantCookieName(testId);

  // set or reset participantID
  if (cookies[participantCookieName] !== value.participant) {
    setCookie(context, participantCookieName, value.participant, {
      expires: addDays(now, 30),
      path: '/',
    });
  }
};

type RemoveTestCookieParameters = {
  context?: GetServerSidePropsContext<ParsedUrlQuery>;
  testId: string;
  removeCookie: DestroyCookie;
};

export const removeTestCookies = ({
  context,
  testId,
  removeCookie,
}: RemoveTestCookieParameters) => {
  removeCookie(context, getParticipantCookieName(testId), {
    path: '/',
  });
  removeCookie(context, getTestCookieName(testId), {
    path: '/',
  });
};

type TestRulesetFuncProps = {
  testId: string;
  tenantId: string;
  context?: GetServerSidePropsContext<ParsedUrlQuery>;
  cookies: {
    [x: string]: any;
  };
  setCookie?: SetCookie;
  removeCookie?: DestroyCookie;
};

export const getTestRulesetFromAPI = async ({
  testId,
  tenantId,
  cookies,
  setCookie,
  removeCookie,
  context,
}: TestRulesetFuncProps) => {
  const participantID = cookies[getParticipantCookieName(testId)] ?? '';
  let testRuleset: TestRuleset;

  // build get url with testId and client
  let getUrl = `${apiUrl.replace(
    '<env>',
    process.env.ENVIRONMENT === 'staging' ? 'dev' : 'cloud',
  )}/servlet/Participant?client=${encodeURIComponent(tenantId)}&test=${testId}`;
  // if participant for the testId is in cookie, add it to the get url
  if (participantID) {
    getUrl = `${getUrl}&participant=${participantID}`;
  }
  let response: Response;
  try {
    response = await fetch(getUrl, {
      headers: { 'Content-Type': 'application/json' },
    });
  } catch (e) {
    return undefined;
  }
  const data = await response.json();

  if (typeof setCookie !== 'undefined') {
    if (data.participant) {
      // create test ruleset from data
      testRuleset = {
        participant: data.participant,
        variant: data.variant,
      };

      setTestCookies({ context, testId, cookies, setCookie, value: testRuleset });
    } else if (typeof removeCookie !== 'undefined') {
      removeTestCookies({ context, testId, removeCookie });
    }
  }

  return testRuleset;
};

export const getCachedTestRuleset = async ({
  testId,
  tenantId,
  context,
  cookies,
  setCookie,
  removeCookie,
}: TestRulesetFuncProps): Promise<TestRuleset> => {
  try {
    return JSON.parse(cookies[getTestCookieName(testId)]);
  } catch (e) {
    // ignore
  }

  return getTestRulesetFromAPI({
    testId,
    tenantId,
    cookies: cookies || nookieParseCookies(context),
    setCookie: setCookie || nookieSetCookie,
    removeCookie: removeCookie || nookieDestroyCookie,
    context,
  });
};

export const useAbTesting = () => {
  const { destroyCookie, getCookies, setCookie } = useCookies();
  const { tenantId: client } = useConfig();
  const cookies = getCookies();
  // const isConsent = useCookieConsent("C0002");

  /**
   * If you call this function for the first time in the customer journey a participantID for the user is generated and the test for the user is started.
   * The internal handling generates an API call if there is no data from the 'test_<testid>' cookie. Otherwise, the data from the cookie is used to minimize API calls. The test-cookie expires after 24h.
   * This function is async. Please await the resultSet.
   * @param testId the testId as communicated in the test manifest
   * @returns the ruleset for the testId
   */
  const getTestRuleset = async (testId: string) =>
    getCachedTestRuleset({
      testId,
      tenantId: client,
      cookies,
      setCookie,
      removeCookie: destroyCookie,
    });

  /**
   * Adds a `productId` to internal list of relevant products for the `testId`.
   * @param testId the testId as communicated in the test manifest
   * @param sku productId
   * @param group group relevant skus per test. This is used to segment the default outcomes per group (e.g. RPP_tr[reco1] )
   */
  const setRelevantProduct = (testId: string, sku: string | string[], group?: string) => {
    const relevantPrdouct = localStorage.getItem(testId);

    if (!relevantPrdouct) {
      localStorage.setItem(testId, JSON.stringify(typeof sku === 'string' ? [sku] : sku));
    } else {
      const products = JSON.parse(relevantPrdouct);
      // filter unique elements only
      const uniqueProducts = products
        .concat(typeof group !== 'undefined' ? `${sku}|${group}` : sku)
        .filter((v: any, i: any, a: string | any[]) => a.indexOf(v) === i);
      localStorage.setItem(testId, JSON.stringify(uniqueProducts));
    }
  };

  /**
   * Returns a boolean for the `productId` if it was previously marked relevant for the `testId`.
   * @param testId the testId as communicated in the test manifest
   * @param sku productId
   * @returns an array of [sku, group] arrays that match the sku parameter
   */
  const getRelevantProduct = (testId: string, sku: string): (string | undefined)[][] => {
    const skuList: Array<string> = JSON.parse(localStorage.getItem(testId) || '[]');
    const groupedProducts = skuList.map((entry) => entry.split('|'));
    return groupedProducts.filter((product) => product[0] === sku) || [];
  };

  /**
   * Call the API and set an Outcome with the values from the payload. Internally adds a value for 'SESSIONID' from the value in the SESSIONID-Cookie.
   * @param {string} testId the testId as communicated in the test manifest
   * @param payload object of key/value pairs of type Payload
   * @returns void
   */
  const setOutcome = (testId: string, payload: Payload) => {
    const conv = ['SESSIONID'];
    const val = [cookies.SESSIONID];

    if (!cookies || !cookies[`${getTestCookieName(testId)}_participant`]) {
      return;
    }
    // generate payload for POST
    const apiPayload: APIPayload = {
      client,
      test: testId,
      uuid: cookies[`${getTestCookieName(testId)}_participant`],
      conversion: conv.concat(Object.keys(payload)),
      value: val.concat(Object.values(payload) as string[]),
      'User-Agent': navigator.userAgent,
    };

    fetch(`${apiUrl}/servlet/Conversion`, {
      method: 'POST',
      body: JSON.stringify(apiPayload),
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
      },
    });
  };

  /**
   * Tracks a outcome for the `productId` for all active tests. Checks if the productId was marked as relevant per test and adds the '_tr' prefix plus the test `variant` to the `template` if true.
   * @param sku productId
   * @param masterId masterId (AKL)
   * @param template the template from which the function is called
   */
  const setDefaultViewOutcome = (sku: string, masterId: string, template: Template = 'RPP') => {
    const tests = Object.keys(cookies).filter(
      (key) => key.startsWith(cookiePrefix) && key.indexOf('_participant') === -1,
    );

    tests.forEach((prefixedTestId) => {
      const testId = prefixedTestId.replace(cookiePrefix, '');
      // get relevant product status
      const relevantProduct = getRelevantProduct(testId, sku)[0]; // use the first match?
      let isProductRelevant = !!relevantProduct;
      const [, group] = relevantProduct || [];

      // if sku is relevant just add masterId to relevant products
      if (isProductRelevant) {
        setRelevantProduct(testId, masterId, group);
        // else check if AKL was previously marked as relevant and update isRelevantProduct
      } else {
        isProductRelevant = getRelevantProduct(testId, masterId).length > 0;
      }

      const outcomePayload: OutcomePayload = {};
      outcomePayload[
        isProductRelevant ? `${template}_tr${group === undefined ? '' : `[${group}]`}` : template
      ] = '1';

      // call setTestOutcome for the test
      setOutcome(testId, outcomePayload);
    });
  };

  /**
   *
   * @param products Array of all products for the tracking. Testrelevant products are calculated with the internal list of relevant products.
   * @param template the template from which the function is called.
   * @param orderId optional id of the current order.
   */
  const setDefaultProductOutcome = (
    products: Product[],
    template: Template,
    orderId: string | undefined = undefined,
  ) => {
    // find all tests in cookies
    const tests = Object.keys(cookies).filter(
      (key) => key.startsWith(cookiePrefix) && key.indexOf('_participant') === -1,
    );

    // generate RTH-Value and RTH-Count
    let rthValue = 0;
    let rthCount = 0;

    // add up total values for all products
    products.forEach((article) => {
      rthValue += article.value;
      rthCount += article.count;
    });

    tests.forEach((prefixedTestId) => {
      const testId = prefixedTestId.replace(cookiePrefix, '');
      // get list off all products for the test
      const skuList: Array<string> = JSON.parse(localStorage.getItem(testId) || '[]');
      const groupedProducts = skuList.map((entry) => entry.split('|'));
      const groups = groupedProducts
        .map((p) => p[1])
        .filter((v: any, i: any, a: string | any[]) => a.indexOf(v) === i);

      // calculate RTH-Value_tr and RTH-Count_tr for the test
      const results: {
        group?: string;
        val: number;
        count: number;
      }[] = [];
      // for each group ..
      groups.forEach((group) => {
        const result = {
          group,
          val: 0,
          count: 0,
        };
        // .. iterate each product and add up values and counts for relevant product
        products.forEach((article) => {
          if (
            groupedProducts.filter((g) => g[0] === article.sku && g[1] === group).length > 0 ||
            (article.masterId !== undefined &&
              groupedProducts.filter((g) => g[0] === article.masterId && g[1] === group).length > 0)
          ) {
            // for AKL case when sku is not in relevant products. We just add it for all. List is unique.
            setRelevantProduct(testId, article.sku);
            result.val += article.value;
            result.count += article.count;
          }
        });

        results.push(result);
      });

      // generate payload for the test
      const outcomePayload: OutcomePayload = {};
      outcomePayload[`${template}_VALUE`] = rthValue * 100;
      outcomePayload[`${template}_COUNT`] = rthCount;
      // iterate groups and generate unique entries per group or at least one unnamed entry if there is only an 'undefined' group
      results.forEach((result) => {
        outcomePayload[
          `${template}_VALUE_tr${result.group !== undefined ? `[${result.group}]` : ''}`
        ] = result.val;
        outcomePayload[
          `${template}_COUNT_tr${result.group !== undefined ? `[${result.group}]` : ''}`
        ] = result.count;
      });
      if (orderId && orderId !== '') {
        outcomePayload.ORDERID = orderId;
      }

      // call setTestOutcome for the test
      setOutcome(testId, outcomePayload);
    });
  };

  const setDefaultCustomerOutcome = (customertype: string) => {
    const tests = Object.keys(cookies).filter(
      (key) => key.startsWith(cookiePrefix) && key.indexOf('_participant') === -1,
    );
    tests.forEach((prefixedTestId) => {
      const testId = prefixedTestId.replace(cookiePrefix, '');

      const outcomePayload = {
        CUSTOMERTYPE: customertype,
      };

      // call setTestOutcome for the test
      setOutcome(testId, outcomePayload);
    });
  };

  return {
    getTestRuleset,
    setRelevantProduct,
    getRelevantProduct,
    setOutcome,
    setDefaultProductOutcome,
    setDefaultViewOutcome,
    setDefaultCustomerOutcome,
  };
};

/**
 * useMousemovement hook
 *
 * Use to find out, if the mouse has been moved at least once.
 * Excludes mouse movement, if the userAgent includes `www.empiriecom.com`.
 *
 * @returns `mouseMoved: true` if the mouse has been moved at least once on the page, `false` otherwise
 */
export const useMousemovement = () => {
  const [mouseMoved, setMouseMoved] = useState(false);

  const move = () => {
    if (!mouseMoved) {
      setMouseMoved(true);
    }
  };

  useEffect(() => {
    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
      const { userAgent } = navigator;
      if (!userAgent.toLowerCase().includes('www.empiriecom.com')) {
        document.addEventListener('mousemove', move, { once: true });
        document.addEventListener('touchstart', move, { once: true });
      }
    }
    return () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('touchstart', move);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { mouseMoved };
};
export const isAbTestParticipant = ({
  testCookie,
  customTestRecognizerFunction,
}: {
  testCookie: string;
  customTestRecognizerFunction?: () => boolean;
}) => {
  if (testCookie) {
    return !!testCookie;
  }
  if (customTestRecognizerFunction) {
    return customTestRecognizerFunction();
  }
  return false;
};
